import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRightLeft,
  CalendarDays,
  Clock3,
  Pencil,
  Trash2,
} from "lucide-react";

import AdminHeader from "@/components/admin/AdminHeader";
import { useAuth } from "@/contexts/AuthContext";
import {
  deleteAdminGuestRider,
  getAdminGuestRiders,
  upsertAdminGuestRider,
} from "@/api/adminRiders";
import { getConfiguredSeason } from "@/lib/season";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatSvDateTime, toDate } from "@/utils/date";

const defaultGuestForm = {
  id: null,
  officialMatchId: "",
  riderId: "",
  guestForTeamId: "",
  guestNote: "",
};

function formatDateTime(iso) {
  if (!iso) return "-";
  return formatSvDateTime(iso, { dateStyle: "short", timeStyle: "short" }) || "-";
}

function isPastMatch(matchDate) {
  const date = toDate(matchDate);
  if (!date || Number.isNaN(date.getTime())) return false;
  return date.getTime() < Date.now();
}

export default function AdminGuestRidersPage() {
  const { user } = useAuth();
  const [guestSeason, setGuestSeason] = useState(getConfiguredSeason());
  const [guestForm, setGuestForm] = useState(defaultGuestForm);
  const [guestRidersData, setGuestRidersData] = useState({
    items: [],
    riders: [],
    teams: [],
    matches: [],
  });
  const [loadingGuestRiders, setLoadingGuestRiders] = useState(false);
  const [savingGuestRider, setSavingGuestRider] = useState(false);

  const guestSeasonOptions = useMemo(() => {
    const baseSeason = Number(getConfiguredSeason());
    return [baseSeason + 1, baseSeason, baseSeason - 1, baseSeason - 2, baseSeason - 3]
      .filter((value, index, array) => array.indexOf(value) === index)
      .map(String);
  }, []);

  const selectedGuestRider = useMemo(
    () =>
      (guestRidersData.riders || []).find(
        (item) => String(item?.id || "") === String(guestForm.riderId || "")
      ) || null,
    [guestForm.riderId, guestRidersData.riders]
  );

  const selectedGuestMatch = useMemo(
    () =>
      (guestRidersData.matches || []).find(
        (item) => String(item?.id || "") === String(guestForm.officialMatchId || "")
      ) || null,
    [guestForm.officialMatchId, guestRidersData.matches]
  );

  const availableGuestTeams = useMemo(() => {
    const riderTeamId = String(selectedGuestRider?.team_id || "");
    const allowedTeamIds = [
      String(selectedGuestMatch?.home_team_id || ""),
      String(selectedGuestMatch?.away_team_id || ""),
    ].filter(Boolean);
    return (guestRidersData.teams || []).filter((team) => {
      const teamId = String(team?.id || "");
      return teamId && allowedTeamIds.includes(teamId) && teamId !== riderTeamId;
    });
  }, [
    guestRidersData.teams,
    selectedGuestMatch?.away_team_id,
    selectedGuestMatch?.home_team_id,
    selectedGuestRider?.team_id,
  ]);

  const sortedAssignments = useMemo(() => {
    return [...(guestRidersData.items || [])].sort((a, b) => {
      const aTime = toDate(a?.match_date)?.getTime?.() || 0;
      const bTime = toDate(b?.match_date)?.getTime?.() || 0;
      return aTime - bTime;
    });
  }, [guestRidersData.items]);

  const currentAssignments = useMemo(
    () => sortedAssignments.filter((item) => !isPastMatch(item?.match_date)),
    [sortedAssignments]
  );

  const pastAssignments = useMemo(
    () => sortedAssignments.filter((item) => isPastMatch(item?.match_date)).reverse(),
    [sortedAssignments]
  );

  useEffect(() => {
    if (user?.role !== "super_admin") return;

    let mounted = true;
    setLoadingGuestRiders(true);
    getAdminGuestRiders(guestSeason)
      .then((res) => {
        if (!mounted) return;
        setGuestRidersData({
          items: Array.isArray(res?.items) ? res.items : [],
          riders: Array.isArray(res?.riders) ? res.riders : [],
          teams: Array.isArray(res?.teams) ? res.teams : [],
          matches: Array.isArray(res?.matches) ? res.matches : [],
        });
      })
      .catch((err) => {
        toast.error(err?.message || "Kunde inte hämta matchinlåningar");
      })
      .finally(() => {
        if (mounted) setLoadingGuestRiders(false);
      });

    return () => {
      mounted = false;
    };
  }, [guestSeason, user?.role]);

  if (user?.role !== "super_admin") {
    return <Navigate to="/admin" replace />;
  }

  const refreshGuestRiders = async () => {
    const res = await getAdminGuestRiders(guestSeason);
    setGuestRidersData({
      items: Array.isArray(res?.items) ? res.items : [],
      riders: Array.isArray(res?.riders) ? res.riders : [],
      teams: Array.isArray(res?.teams) ? res.teams : [],
      matches: Array.isArray(res?.matches) ? res.matches : [],
    });
  };

  const handleEditGuestRider = (item) => {
    setGuestForm({
      id: String(item?.id || ""),
      officialMatchId: String(item?.official_match_id || ""),
      riderId: String(item?.rider_id || ""),
      guestForTeamId: String(item?.guest_for_team_id || ""),
      guestNote: String(item?.guest_note || ""),
    });
  };

  const handleDeleteGuestRider = async (item) => {
    if (savingGuestRider) return;
    const confirmed = window.confirm("Vill du ta bort den här inlåningen?");
    if (!confirmed) return;
    setSavingGuestRider(true);
    try {
      await deleteAdminGuestRider(String(item?.id || ""));
      await refreshGuestRiders();
      if (String(guestForm.id || "") === String(item?.id || "")) {
        setGuestForm(defaultGuestForm);
      }
      toast.success("Inlåningen togs bort.");
    } catch (err) {
      toast.error(err?.message || "Kunde inte ta bort inlåningen");
    } finally {
      setSavingGuestRider(false);
    }
  };

  const handleGuestSubmit = async (event) => {
    event.preventDefault();
    if (savingGuestRider) return;
    if (!guestForm.officialMatchId) {
      toast.error("Välj en match.");
      return;
    }
    if (!guestForm.riderId) {
      toast.error("Välj en förare.");
      return;
    }
    if (!guestForm.guestForTeamId) {
      toast.error("Välj vilket lag som lånar föraren.");
      return;
    }

    setSavingGuestRider(true);
    try {
      await upsertAdminGuestRider({
        id: guestForm.id || null,
        official_match_id: String(guestForm.officialMatchId || ""),
        rider_id: String(guestForm.riderId || ""),
        guest_for_team_id: String(guestForm.guestForTeamId || ""),
        guest_note: String(guestForm.guestNote || ""),
      });
      await refreshGuestRiders();
      setGuestForm(defaultGuestForm);
      toast.success("Inlåningen sparades.");
    } catch (err) {
      toast.error(err?.message || "Kunde inte spara inlåningen");
    } finally {
      setSavingGuestRider(false);
    }
  };

  return (
    <div className="min-h-screen">
      <AdminHeader />
      <main className="container mx-auto px-4 py-6">
        <div className="space-y-6">
          <Card className="overflow-hidden border-border/70 shadow-sm">
            <CardHeader className="relative gap-4 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white">
              <div className="absolute inset-y-0 right-0 w-32 bg-[radial-gradient(circle_at_top,rgba(245,158,11,0.24),transparent_65%)]" />
              <div className="relative flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                      <ArrowRightLeft className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-xl text-white sm:text-2xl">
                        Matchinlåning
                      </CardTitle>
                      <CardDescription className="mt-1 max-w-2xl text-slate-200">
                        Tilldela en förare till en specifik match utan att röra ordinarie klubbtillhörighet eller säsongstrupp.
                      </CardDescription>
                    </div>
                  </div>
                </div>
                <div className="grid gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 sm:min-w-[170px]">
                  <Label htmlFor="guest-season" className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-300">
                    Säsong
                  </Label>
                  <Select
                    value={String(guestSeason)}
                    onValueChange={(value) => {
                      setGuestSeason(Number(value));
                      setGuestForm(defaultGuestForm);
                    }}
                  >
                    <SelectTrigger id="guest-season" className="border-white/10 bg-white/10 text-white">
                      <SelectValue placeholder="Säsong" />
                    </SelectTrigger>
                    <SelectContent className="border bg-card shadow-lg">
                      {guestSeasonOptions.map((season) => (
                        <SelectItem key={season} value={season}>
                          {season}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
          </Card>

          <section className="grid gap-6 xl:grid-cols-[minmax(360px,0.95fr)_minmax(0,1.05fr)]">
            <Card className="border-border/70 shadow-sm">
              <CardHeader>
                <CardTitle>{guestForm.id ? "Redigera inlåning" : "Ny inlåning"}</CardTitle>
                <CardDescription>
                  Välj match, förare och vilket lag som lånar föraren i just den matchen.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleGuestSubmit} className="space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="guest-match">Match</Label>
                    <Select
                      value={guestForm.officialMatchId}
                      onValueChange={(value) =>
                        setGuestForm((prev) => ({
                          ...prev,
                          officialMatchId: value,
                          guestForTeamId: "",
                        }))
                      }
                    >
                      <SelectTrigger id="guest-match">
                        <SelectValue placeholder="Välj match" />
                      </SelectTrigger>
                      <SelectContent className="border bg-card shadow-lg">
                        {(guestRidersData.matches || []).map((match) => (
                          <SelectItem key={match.id} value={String(match.id)}>
                            {match.title} · {formatDateTime(match.date)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="guest-rider">Förare</Label>
                    <Select
                      value={guestForm.riderId}
                      onValueChange={(value) =>
                        setGuestForm((prev) => ({
                          ...prev,
                          riderId: value,
                          guestForTeamId: "",
                        }))
                      }
                    >
                      <SelectTrigger id="guest-rider">
                        <SelectValue placeholder="Välj förare" />
                      </SelectTrigger>
                      <SelectContent className="border bg-card shadow-lg">
                        {(guestRidersData.riders || []).map((rider) => (
                          <SelectItem key={rider.id} value={String(rider.id)}>
                            {rider.name} · {rider.team_name || "-"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedGuestRider ? (
                    <div className="rounded-xl border bg-muted/20 px-3 py-3 text-sm text-muted-foreground">
                      <div>
                        <span className="font-medium text-foreground">Ordinarie lag:</span>{" "}
                        {selectedGuestRider.team_name || "-"}
                      </div>
                      {selectedGuestMatch ? (
                        <div className="mt-1">
                          <span className="font-medium text-foreground">Vald match:</span>{" "}
                          {selectedGuestMatch.title || "-"}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="grid gap-2">
                    <Label htmlFor="guest-team">Inlånad till lag</Label>
                    <Select
                      value={guestForm.guestForTeamId}
                      onValueChange={(value) =>
                        setGuestForm((prev) => ({ ...prev, guestForTeamId: value }))
                      }
                      disabled={!guestForm.riderId || !guestForm.officialMatchId}
                    >
                      <SelectTrigger id="guest-team">
                        <SelectValue placeholder="Välj lag" />
                      </SelectTrigger>
                      <SelectContent className="border bg-card shadow-lg">
                        {availableGuestTeams.map((team) => (
                          <SelectItem key={team.id} value={String(team.id)}>
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="guest-note">Anteckning (valfritt)</Label>
                    <Input
                      id="guest-note"
                      value={guestForm.guestNote}
                      onChange={(event) =>
                        setGuestForm((prev) => ({ ...prev, guestNote: event.target.value }))
                      }
                      placeholder="Ex. inlånad för kvällens match"
                    />
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={savingGuestRider}>
                      {savingGuestRider ? "Sparar..." : "Spara inlåning"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setGuestForm(defaultGuestForm)}
                      disabled={savingGuestRider}
                    >
                      Rensa
                    </Button>
                    {guestForm.id ? (
                      <Button
                        type="button"
                        variant="outline"
                        className="gap-2"
                        onClick={() => handleDeleteGuestRider({ id: guestForm.id })}
                        disabled={savingGuestRider}
                      >
                        <Trash2 className="h-4 w-4" />
                        Ta bort
                      </Button>
                    ) : null}
                  </div>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="border-border/70 shadow-sm">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Clock3 className="h-4 w-4 text-primary" />
                    <CardTitle>Aktuella och kommande</CardTitle>
                  </div>
                  <CardDescription>
                    Matchinlåningar som fortfarande är relevanta för pågående eller kommande matcher.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {loadingGuestRiders ? (
                    <p className="text-sm text-muted-foreground">Hämtar matchinlåningar...</p>
                  ) : currentAssignments.length ? (
                    currentAssignments.map((item) => (
                      <div
                        key={item.id}
                        className="rounded-2xl border border-border/70 bg-card px-4 py-4 shadow-sm"
                      >
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="text-sm font-semibold text-foreground">{item.name}</div>
                              <Badge className="bg-amber-500 text-white hover:bg-amber-500">
                                Aktiv för match
                              </Badge>
                            </div>
                            <div className="mt-2 text-sm font-medium text-foreground">
                              {item.match_title || "Match saknas"}
                            </div>
                            <div className="mt-2 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                              <div>
                                <span className="font-medium text-foreground">Ordinarie lag:</span>{" "}
                                {item.team_name || "-"}
                              </div>
                              <div>
                                <span className="font-medium text-foreground">Lånas av:</span>{" "}
                                {item.guest_for_team_name || "-"}
                              </div>
                              <div className="sm:col-span-2">
                                <span className="font-medium text-foreground">Matchdatum:</span>{" "}
                                {formatDateTime(item.match_date)}
                              </div>
                            </div>
                            {item.guest_note ? (
                              <div className="mt-2 text-sm leading-relaxed text-muted-foreground">
                                {item.guest_note}
                              </div>
                            ) : null}
                          </div>
                          <div className="flex shrink-0 flex-col gap-2 sm:min-w-[160px]">
                            <Button type="button" variant="outline" onClick={() => handleEditGuestRider(item)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Redigera
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => handleDeleteGuestRider(item)}
                              disabled={savingGuestRider}
                            >
                              Ta bort tilldelning
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-dashed border-border/70 bg-muted/20 px-4 py-5 text-sm text-muted-foreground">
                      Inga aktiva eller kommande matchinlåningar för säsongen.
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-border/70 shadow-sm">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-muted-foreground" />
                    <CardTitle>Tidigare matcher</CardTitle>
                  </div>
                  <CardDescription>
                    Passerade matchinlåningar ligger kvar för historik, men tonas ned så de inte tar fokus.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {loadingGuestRiders ? (
                    <p className="text-sm text-muted-foreground">Hämtar historik...</p>
                  ) : pastAssignments.length ? (
                    pastAssignments.map((item) => (
                      <div
                        key={item.id}
                        className="flex flex-col gap-3 rounded-xl border border-border/50 bg-muted/20 px-3 py-3 text-sm md:flex-row md:items-center md:justify-between"
                      >
                        <div className="min-w-0 space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-foreground">{item.name}</span>
                            <span className="text-muted-foreground">→</span>
                            <span className="text-foreground">{item.guest_for_team_name || "-"}</span>
                          </div>
                          <div className="text-muted-foreground">
                            {item.match_title || "Match saknas"} · {formatDateTime(item.match_date)}
                          </div>
                        </div>
                        <div className="flex shrink-0 gap-2">
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleEditGuestRider(item)}>
                            Redigera
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteGuestRider(item)}
                            disabled={savingGuestRider}
                          >
                            Ta bort
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-dashed border-border/70 bg-muted/10 px-4 py-5 text-sm text-muted-foreground">
                      Ingen äldre matchinlåningshistorik för vald säsong ännu.
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-border/70 shadow-sm">
                <CardContent className="flex flex-col gap-3 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-sm font-medium text-foreground">Tillbaka till adminöversikt</div>
                    <div className="text-sm text-muted-foreground">
                      Övriga adminverktyg ligger kvar på översiktssidan.
                    </div>
                  </div>
                  <Button asChild variant="outline">
                    <Link to="/admin">Öppna översikt</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
